/**
 * File: knowledgeBaseService.ts
 * Description: API client for the shared knowledge base (guideline documents
 *   the agents can retrieve, plus drag-and-drop upload to the indexed blob
 *   prefix).
 * Author: GitHub Copilot
 * Created: 2026-05-31
 * Last Modified: 2026-05-31
 */

import { apiClient } from './apiClient';

const KNOWLEDGE_BASE_ENDPOINT = '/api/knowledge-base';

/** Bucket for documents not tied to a specific line of business. */
export const GENERAL_LINE_OF_BUSINESS = 'General';

export interface KnowledgeBaseDocument {
  id: string;
  name: string;
  description?: string | null;
  path?: string | null;
  url?: string | null;
  source?: string;
  category?: string;
  sector?: string | null;
  line_of_business?: string;
  keywords?: string[];
  last_modified?: string | null;
}

interface KnowledgeBaseListResponse {
  documents: KnowledgeBaseDocument[];
}

export interface KnowledgeBaseUploadResponse {
  id: string;
  name: string;
  blob_path: string;
  url: string;
  line_of_business?: string;
  message: string;
  duplicate?: boolean;
}

export interface KnowledgeBaseReindexResponse {
  started: boolean;
  message: string;
}

export interface KnowledgeBaseIndexerStatusResponse {
  status: string;
}

export class KnowledgeBaseService {
  /**
   * Fetch every document the agents can retrieve from the knowledge base.
   */
  static async listDocuments(): Promise<KnowledgeBaseDocument[]> {
    const response = await apiClient.get<KnowledgeBaseListResponse>(
      `${KNOWLEDGE_BASE_ENDPOINT}/documents`,
    );
    return response.documents ?? [];
  }

  /**
   * Upload a file into the knowledge-base blob store under the given line of
   * business. The AI Search indexer picks it up on the next run.
   */
  static async uploadDocument(
    file: File,
    lineOfBusiness: string = GENERAL_LINE_OF_BUSINESS,
  ): Promise<KnowledgeBaseUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('line_of_business', lineOfBusiness);
    return apiClient.postFormData<KnowledgeBaseUploadResponse>(
      `${KNOWLEDGE_BASE_ENDPOINT}/upload`,
      formData,
    );
  }

  /**
   * Fetch the line-of-business choices available for guideline uploads
   * (the profile line-of-business options plus "General").
   */
  static async getLineOfBusinessOptions(): Promise<string[]> {
    const response = await apiClient.get<{ options: string[] }>(
      `${KNOWLEDGE_BASE_ENDPOINT}/line-of-business-options`,
    );
    return response.options ?? [];
  }

  /**
   * Manually trigger a run of the guidelines indexer to refresh the index
   * after uploading documents.
   */
  static async triggerReindex(): Promise<KnowledgeBaseReindexResponse> {
    return apiClient.post<KnowledgeBaseReindexResponse>(
      `${KNOWLEDGE_BASE_ENDPOINT}/reindex`,
      {},
    );
  }

  /**
   * Report whether the guidelines indexer is currently running.
   */
  static async getReindexStatus(): Promise<KnowledgeBaseIndexerStatusResponse> {
    return apiClient.get<KnowledgeBaseIndexerStatusResponse>(
      `${KNOWLEDGE_BASE_ENDPOINT}/reindex/status`,
    );
  }
}

export const knowledgeBaseService = KnowledgeBaseService;
